import { Alert, Box, Card, Chip, Divider, Stack, Typography } from '@mui/material';
import React from 'react';
import { quickBookMoney, quickBookPricingSnapshot } from './QuickBookPricing';

const AmountRow = ({ label, value, currency, emphasis = false, color = 'text.primary', strike = false, testId }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 2, py: 0.6 }}>
    <Typography color="text.secondary" sx={{ fontSize: '0.75rem' }}>{label}</Typography>
    <Typography data-testid={testId} color={color} sx={{ fontSize: emphasis ? '0.85rem' : '0.75rem', fontWeight: emphasis ? 700 : 600, textAlign: 'right', textDecoration: strike ? 'line-through' : 'none' }}>
      {quickBookMoney(value, currency)}
    </Typography>
  </Box>
);

const QuickBookPaymentDescription = ({ booking }) => {
  const snapshot = quickBookPricingSnapshot(booking);
  if (!snapshot) {
    return (
      <Box data-testid="quick-book-payment-description" sx={{ p: 2 }}>
        <Alert severity="warning">Quick Book commercial snapshot is unavailable for this booking.</Alert>
      </Box>
    );
  }

  const rateLabel = Number.isFinite(snapshot.serviceGstRatePercent)
    ? `${snapshot.serviceGstRatePercent}% on taxable base`
    : 'included in customer gross';
  const payout = snapshot.payoutBreakdown;

  return (
    <Box data-testid="quick-book-payment-description" sx={{ bgcolor: '#F5F5F5', p: { xs: 1.25, sm: 2 } }}>
      <Stack spacing={1.5}>
        <Card variant="outlined" sx={{ p: 1.5 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
            <Typography component="h3" sx={{ fontSize: '0.85rem', fontWeight: 700 }}>Quick Book customer pricing</Typography>
            <Chip size="small" color="primary" variant="outlined" label="Server snapshot" />
          </Stack>
          {snapshot.offerApplied && (
            <AmountRow label="Quick Book Regular Price" value={snapshot.regularGrossPaise} currency={snapshot.currency} strike testId="quick-book-detail-regular-gross" />
          )}
          {snapshot.offerApplied && (
            <AmountRow label="You Save" value={snapshot.savingsPaise} currency={snapshot.currency} color="success.main" />
          )}
          <Divider sx={{ my: 0.5 }} />
          <AmountRow label="Taxable service base" value={snapshot.effectiveBasePaise} currency={snapshot.currency} />
          <AmountRow label={`Service GST (${rateLabel})`} value={snapshot.serviceGstPaise} currency={snapshot.currency} />
          <AmountRow label={snapshot.offerApplied ? 'Quick Book Offer Price' : 'Quick Book Regular Price'} value={snapshot.effectiveGrossPaise} currency={snapshot.currency} emphasis color={snapshot.offerApplied ? 'success.main' : 'text.primary'} testId="quick-book-detail-effective-gross" />
        </Card>

        <Card variant="outlined" sx={{ p: 1.5 }}>
          <Typography component="h3" sx={{ fontSize: '0.85rem', fontWeight: 700, mb: 0.75 }}>Expert payout snapshot</Typography>
          {payout && <AmountRow label="Regular service base" value={payout.regularServiceBaseTotalPaise} currency={snapshot.currency} />}
          {payout && Number(payout.customerDiscountTaxableTotalPaise) > 0 && <AmountRow label="CuDel-funded taxable discount" value={payout.customerDiscountTaxableTotalPaise} currency={snapshot.currency} />}
          {payout && <AmountRow label="CuDel commission" value={payout.platformCommissionTotalPaise} currency={snapshot.currency} />}
          {payout && <AmountRow label="Commission GST" value={payout.commissionGstTotalPaise} currency={snapshot.currency} />}
          {payout && Number(payout.tcsTotalPaise) > 0 && <AmountRow label="TCS" value={payout.tcsTotalPaise} currency={snapshot.currency} />}
          <Divider sx={{ my: 0.5 }} />
          <AmountRow label="Final expert payout" value={snapshot.providerPayoutPaise} currency={snapshot.currency} emphasis color="success.main" />
          {snapshot.taxLiabilityMode && (
            <Typography color="text.secondary" sx={{ mt: 0.75, fontSize: '0.7rem' }}>
              Liability mode: {snapshot.taxLiabilityMode}. Values are immutable booking-time snapshots.
            </Typography>
          )}
        </Card>
      </Stack>
    </Box>
  );
};

export default QuickBookPaymentDescription;
